import { Box, Grid, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { FileAPI } from "../../../api";
import BrandImageUpload from "../../../components/core/UploadFile/BrandImageUpload";
import { Organization } from "../../../model";

interface ManageBrandProps {
  organization: Organization;
  disabled?: boolean | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (err: any) => void;
  buttonAlignment?: string;
  alignment?: "left" | "right" | "center" | "space-between";
  spacing?: number;
  cellSize?: number | string;
  onUpdate?: (
    favicon: string | undefined,
    shortLogo: string | undefined,
    longLogo: string | undefined,
  ) => void;
}

export const ManageBrand: FC<ManageBrandProps> = ({
  organization: sourceOrganization,
  disabled,
  onError,
  buttonAlignment,
  onUpdate,
  alignment = "space-between",
  spacing = 2,
  cellSize = "180px",
}) => {
  // State to hold the organization logo
  const [logo, setLogo] = useState<string | undefined>(
    sourceOrganization.theme?.shortLogoUrl,
  );

  // State to hold the organization's long logo
  const [longLogo, setLongLogo] = useState<string | undefined>(
    sourceOrganization.theme?.longLogoUrl,
  );

  // State to hold the organization's favicon
  const [favicon, setFavicon] = useState<string | undefined>(
    sourceOrganization.theme?.faviconUrl,
  );

  useEffect(() => {
    setLogo(sourceOrganization.theme?.shortLogoUrl);
    setLongLogo(sourceOrganization.theme?.longLogoUrl);
    setFavicon(sourceOrganization.theme?.faviconUrl);
  }, [sourceOrganization]);

  const handleFileUpload = async (
    files: FileList | null,
  ): Promise<string | undefined> => {
    if (!files) {
      return;
    }

    try {
      const value = await FileAPI.uploadFile(files);

      // replacing '/' in keys so they can be used to fetch files
      const keyedFiles = value.map((fileUploadResponse) => {
        return `${process.env.REACT_APP_REST_API}/files/${encodeURIComponent(
          fileUploadResponse.key,
        )}`;
      });

      return keyedFiles[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // upload failed
      console.error(`Error uploading file: ${err}`);
      let error = "Error uploading image";
      if (err.response.status === 413) {
        error = "Image is too large to upload";
      }
      onError?.(error);
      return undefined;
    }
  };

  const handleUploadFavicon = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files).then((url) => {
      setFavicon(url);
      if (onUpdate) onUpdate(url, logo, longLogo);
    });
  };

  const handleUploadShortLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files).then((url) => {
      setLogo(url);
      if (onUpdate) onUpdate(favicon, url, longLogo);
    });
  };

  const handleUploadPrimaryLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files).then((url) => {
      setLongLogo(url);
      if (onUpdate) onUpdate(favicon, logo, url);
    });
  };

  return (
    <Grid
      container
      spacing={spacing} // <-- Apply spacing dynamically
      justifyContent={
        alignment === "left"
          ? "flex-start"
          : alignment === "right"
            ? "flex-end"
            : alignment === "space-between"
              ? "space-between"
              : "center"
      }
    >
      <Grid
        display="flex"
        justifyContent={
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : alignment === "space-between"
                ? "flex-start"
                : "center"
        }
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">Favicon</Typography>
          {/* Favicon Upload */}
          <BrandImageUpload
            variant="favicon"
            buttonAlignment="right"
            disabled={disabled}
            file={favicon}
            size={cellSize}
            handleDrop={(e) => {
              if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
                handleFileUpload(e.dataTransfer?.files).then((url) => {
                  setFavicon(url);
                  if (onUpdate) onUpdate(favicon, logo, longLogo);
                });
              }
            }}
            handleFileChange={(e) => {
              handleUploadFavicon(e);
            }}
            accept="image/*"
            onRemove={() => {
              setFavicon(undefined);
              if (onUpdate) onUpdate(undefined, logo, longLogo);
            }}
          />
        </Box>
      </Grid>

      <Grid
        display="flex"
        justifyContent={
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : alignment === "space-between"
                ? "center"
                : "center"
        }
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">Short Logo</Typography>
          <BrandImageUpload
            variant="shortLogo"
            buttonAlignment="right"
            disabled={disabled}
            file={logo}
            size={cellSize}
            handleDrop={(e) => {
              if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
                handleFileUpload(e.dataTransfer?.files).then((url) => {
                  setLogo(url);
                  if (onUpdate) onUpdate(favicon, logo, longLogo);
                });
              }
            }}
            handleFileChange={(e) => {
              handleUploadShortLogo(e);
            }}
            accept="image/*"
            onRemove={() => {
              setLogo(undefined);
              if (onUpdate) onUpdate(favicon, undefined, longLogo);
            }}
          />
        </Box>
      </Grid>

      <Grid
        display="flex"
        justifyContent={
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : alignment === "space-between"
                ? "flex-end"
                : "center"
        }
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">Primary Logo</Typography>
          <BrandImageUpload
            onCopyIcon={() => setLongLogo(logo)}
            hideCopyButton={logo ? false : true}
            buttonAlignment="right"
            variant="longLogo"
            disabled={disabled}
            file={longLogo}
            size={cellSize}
            handleDrop={(e) => {
              if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
                handleFileUpload(e.dataTransfer?.files).then((url) => {
                  setLongLogo(url);
                  if (onUpdate) onUpdate(favicon, logo, longLogo);
                });
              }
            }}
            handleFileChange={(e) => {
              handleUploadPrimaryLogo(e);
            }}
            accept="image/*"
            onRemove={() => {
              setLongLogo(undefined);
              if (onUpdate) onUpdate(favicon, logo, undefined);
            }}
          />
          <Box
            sx={{
              my: 1,
              display: "flex",
              justifyContent: buttonAlignment === "right" ? "right" : "left",
            }}
          ></Box>
        </Box>
      </Grid>
    </Grid>
  );
};
